// Manual registration entry (added 19 Oct 2026).
// AddRegistrationModal - a company registered by email / phone / WhatsApp, or a real
//   customer the website spam trap blocked. Same fields as the website form.
// AddParticipantModal - one more staff member on an existing registration.
// Backend: POST /api/trainings/:id/registrations and
//          POST /api/trainings/registrations/:id/participants (routes/trainings.js).
// No email goes to Steph; the client's confirmation is sent only if ticked.
import { useState } from 'react';
import axios from 'axios';

const API = 'https://carriera-backend.onrender.com';
const inp = "w-full border border-carriera-border px-3 py-2 text-sm text-carriera-text bg-white outline-none focus:border-carriera-accent";
const lbl = "block text-xs text-carriera-muted uppercase tracking-widest mb-1";
const emptyPerson = { full_name: '', job_title: '', email: '', phone: '' };
// Same options as the website form (register.html), plus how staff-entered ones usually arrive.
const HOW_FOUND = ['Email Newsletter', 'Social Media', 'Referral', 'Website', 'LinkedIn', 'Previous Participant', 'Phone / WhatsApp / Email to Carriera', 'Other'];

function Shell({ title, subtitle, onClose, busy, children, onSubmit, submitLabel }) {
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => !busy && onClose()}>
      <form onSubmit={onSubmit} className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-carriera-border flex items-start justify-between">
          <div>
            <h3 className="font-serif text-xl font-normal text-carriera-text">{title}</h3>
            {subtitle && <p className="text-xs text-carriera-muted mt-1">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="text-carriera-muted hover:text-carriera-text text-lg">✕</button>
        </div>
        <div className="p-6 space-y-4">
          {children}
          <button type="submit" disabled={busy}
            className="w-full bg-carriera-text text-white text-sm font-medium py-2.5 hover:bg-carriera-accent transition-colors duration-200 disabled:opacity-50">
            {busy ? 'Saving...' : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

function PersonFields({ person, onChange, nameLabel = 'Full name' }) {
  const set = k => e => onChange({ ...person, [k]: e.target.value });
  return (
    <>
      <div>
        <label className={lbl}>{nameLabel} <span className="text-red-500">*</span></label>
        <input required value={person.full_name} onChange={set('full_name')} className={inp} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Job title</label><input value={person.job_title} onChange={set('job_title')} className={inp} /></div>
        <div><label className={lbl}>Phone</label><input value={person.phone} onChange={set('phone')} className={inp} /></div>
      </div>
      <div><label className={lbl}>Email</label><input type="email" value={person.email} onChange={set('email')} className={inp} /></div>
    </>
  );
}

export function AddRegistrationModal({ training, headers, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: '', company_address: '', pic_name: '', pic_job_title: '', pic_email: '', pic_phone: '',
    payment_method: '', how_found: 'Phone / WhatsApp / Email to Carriera',
  });
  const [people, setPeople] = useState([{ ...emptyPerson }]);
  const [sendConfirmation, setSendConfirmation] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  // The contact person is often also attending: copy their details into the first
  // participant row if it is still empty, otherwise add them as a new row.
  function picAttends() {
    const pic = { full_name: form.pic_name, job_title: form.pic_job_title, email: form.pic_email, phone: form.pic_phone };
    setPeople(prev => (prev[0].full_name.trim() ? [...prev, pic] : [pic, ...prev.slice(1)]));
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/trainings/${training.id}/registrations`,
        { ...form, participants: people, send_confirmation: sendConfirmation }, { headers });
      if (sendConfirmation && !res.data.emailed) alert('Saved, but the confirmation email could not be sent. Please email the client yourself.');
      onSaved();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally { setBusy(false); }
  }

  return (
    <Shell title="Add registration" subtitle={training.title} onClose={onClose} busy={busy} onSubmit={submit} submitLabel="Save registration">
      <p className="text-xs text-carriera-muted uppercase tracking-widest">Company</p>
      <div><label className={lbl}>Company name <span className="text-red-500">*</span></label><input required value={form.company_name} onChange={set('company_name')} className={inp} /></div>
      <div><label className={lbl}>Company address</label><textarea rows={2} value={form.company_address} onChange={set('company_address')} className={inp} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Contact person <span className="text-red-500">*</span></label><input required value={form.pic_name} onChange={set('pic_name')} className={inp} /></div>
        <div><label className={lbl}>Their job title</label><input value={form.pic_job_title} onChange={set('pic_job_title')} className={inp} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Their email</label><input type="email" value={form.pic_email} onChange={set('pic_email')} className={inp} /></div>
        <div><label className={lbl}>Their phone</label><input value={form.pic_phone} onChange={set('pic_phone')} className={inp} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Payment</label>
          <select value={form.payment_method} onChange={set('payment_method')} className={inp}>
            <option value="">Not stated</option>
            <option value="HRD Corp Claimable">HRD Corp Claimable</option>
            <option value="Self-Funded">Self-Funded</option>
          </select>
        </div>
        <div>
          <label className={lbl}>How found</label>
          <select value={form.how_found} onChange={set('how_found')} className={inp}>
            {HOW_FOUND.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-carriera-muted uppercase tracking-widest">Participants ({people.length})</p>
        <button type="button" onClick={picAttends} className="text-xs text-carriera-accent hover:underline">Contact person is attending</button>
      </div>
      {people.map((p, i) => (
        <div key={i} className="border border-carriera-border p-3 space-y-3 bg-carriera-bg">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-carriera-sec">Participant {i + 1}</p>
            {people.length > 1 && (
              <button type="button" onClick={() => setPeople(prev => prev.filter((_, j) => j !== i))} className="text-xs text-red-400 hover:text-red-600">Remove</button>
            )}
          </div>
          <PersonFields person={p} onChange={np => setPeople(prev => prev.map((x, j) => (j === i ? np : x)))} />
        </div>
      ))}
      <button type="button" onClick={() => setPeople(prev => [...prev, { ...emptyPerson }])}
        className="w-full text-sm text-carriera-sec border border-dashed border-carriera-border py-2 hover:border-carriera-accent hover:text-carriera-accent">
        + Another participant
      </button>

      <label className="flex items-start gap-2 text-sm text-carriera-text pt-2">
        <input type="checkbox" checked={sendConfirmation} onChange={e => setSendConfirmation(e.target.checked)} className="w-4 h-4 mt-0.5 accent-carriera-accent" />
        <span>Email the usual registration confirmation to the contact person (participants copied)
          <span className="block text-xs text-carriera-muted">Leave unticked if you have already confirmed with them yourself.</span></span>
      </label>
    </Shell>
  );
}

export function AddParticipantModal({ registration, headers, onClose, onSaved }) {
  const [person, setPerson] = useState({ ...emptyPerson });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/api/trainings/registrations/${registration.id}/participants`, person, { headers });
      onSaved();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally { setBusy(false); }
  }

  return (
    <Shell title="Add participant" subtitle={registration.company_name} onClose={onClose} busy={busy} onSubmit={submit} submitLabel="Add participant">
      <PersonFields person={person} onChange={setPerson} />
      <p className="text-xs text-carriera-muted">No email is sent. Tick them as attended after the course to issue their certificate.</p>
    </Shell>
  );
}
